import { useState, useMemo } from 'react';
import type { ApplicationStatus } from '@applicant-reviewer/api';
import { useApplications } from '../../../hooks/queries/useApplications';
import ReviewDeck from '../components/manager/ReviewDeck';

interface ReviewRouteProps {
  initialApplicantId?: string;
  onBack?: () => void;
}

const FILTER_STATUSES: ApplicationStatus[] = ['submitted', 'reviewing', 'interview', 'offered'];

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  reviewing: 'Reviewing',
  interview: 'Interview',
  offered: 'Offered',
  hired: 'Hired',
  benched: 'Benched',
  rejected: 'Rejected',
};

export default function ReviewRoute({ initialApplicantId, onBack }: ReviewRouteProps) {
  const { data: applications, isLoading } = useApplications(undefined, "manager");
  const [selectedStatuses, setSelectedStatuses] = useState<ApplicationStatus[]>(['submitted']);

  const countByStatus = useMemo(() => {
    const counts: Partial<Record<ApplicationStatus, number>> = {};
    for (const app of applications ?? []) {
      counts[app.status as ApplicationStatus] = (counts[app.status as ApplicationStatus] ?? 0) + 1;
    }
    return counts;
  }, [applications]);

  const filtered = useMemo(() => {
    return (applications ?? []).filter(app =>
      selectedStatuses.includes(app.status as ApplicationStatus)
    );
  }, [applications, selectedStatuses]);

  function toggleStatus(status: ApplicationStatus) {
    setSelectedStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center h-screen bg-white">
        <p className="text-sm text-gray-400">Loading applicants…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-gray-200 flex-wrap">
        {FILTER_STATUSES.map(status => (
          <button
            key={status}
            onClick={() => toggleStatus(status)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              selectedStatuses.includes(status)
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {STATUS_LABELS[status]} ({countByStatus[status] ?? 0})
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-400">
          Showing {filtered.length} of {applications?.length ?? 0}
        </span>
      </div>
      <ReviewDeck
        applicants={filtered}
        initialApplicantId={initialApplicantId}
        onBack={onBack}
      />
    </div>
  );
}
